/* eslint-disable no-param-reassign */
/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable no-nested-ternary */
import * as React from 'react';
import { useReadLocalStorage } from 'usehooks-ts';
import {
  UsersIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline';
import { FloxSpinner } from '@tailwindComponents/FloxSpinner';
import { Button } from '../../tailwindComponents/Button';
import InviteTeamMemberDialog from './components/InviteTeamMemberDialog';
import { useAuthHttp } from '../../hooks';
import { PageHeader } from '../../tailwindComponents/PageHeader';
import {
  ACTIVATE_TEAM_MEMBER_URL,
  DEACTIVATE_TEAM_MEMBER_URL,
  LIST_USERS_URL,
} from '../../constants';

interface Data {
  email: string;
  status: string;
  name: string;
  roles: string[];
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

type Order = 'asc' | 'desc';

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (
  a: { [key in Key]: number | string | string[] },
  b: { [key in Key]: number | string | string[] }
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(
  array: readonly T[],
  comparator: (a: T, b: T) => number
) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  { id: 'name' as keyof Data, label: 'Name' },
  { id: 'email' as keyof Data, label: 'Email' },
  { id: 'roles' as keyof Data, label: 'Roles' },
  { id: 'status' as keyof Data, label: 'Status' },
];

export function Team() {
  const [order, setOrder] = React.useState<Order>('asc');
  const [orderBy, setOrderBy] = React.useState<keyof Data>('name');
  const [selected, setSelected] = React.useState<readonly string[]>([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [isInviteDialogOpen, setIsInviteDialogOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const authHttp = useAuthHttp();
  const [rows, setRows] = React.useState<Data[]>([]);
  const localUser: { email: string } | null = useReadLocalStorage('user');

  const pages = [{ name: 'Team', href: '/team', current: true }];

  React.useEffect(() => {
    async function fetchTeamMembers() {
      setIsLoading(true);
      try {
        const { data } = await authHttp.post(LIST_USERS_URL);

        if (data)
          setRows(
            data.users.map((user: { isActive: boolean }) =>
              user.isActive
                ? { ...user, status: 'Active' }
                : { ...user, status: 'Deactivated' }
            )
          );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('error: ', error);
      } finally {
        setIsLoading(false);
      }
    }
    fetchTeamMembers();
  }, []);

  const handleRequestSort = (property: keyof Data) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = rows.map((n) => n.name);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event: React.MouseEvent<unknown>, name: string) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected: readonly string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isSelected = (name: string) => selected.indexOf(name) !== -1;

  const visibleRows = React.useMemo(
    () =>
      stableSort(rows, getComparator(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ),
    [rows, order, orderBy, page, rowsPerPage]
  );

  const fetchStatusChange = async (
    url: string,
    newStatus: 'Active' | 'Deactivated',
    email: string
  ) => {
    try {
      const { status } = await authHttp.post(url, {
        email,
      });

      if (status === 200) {
        const updatedRows = rows.map((row) =>
          row.email === email ? { ...row, status: newStatus } : row
        );
        setRows(updatedRows);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('error: ', error);
    }
  };

  const handleStatusChange = async (
    e: React.MouseEvent<HTMLButtonElement>,
    email: string,
    currentStatus: string
  ) => {
    e.preventDefault();
    e.stopPropagation();
    if (currentStatus === 'Active') {
      fetchStatusChange(DEACTIVATE_TEAM_MEMBER_URL, 'Deactivated', email);
    } else {
      fetchStatusChange(ACTIVATE_TEAM_MEMBER_URL, 'Active', email);
    }
  };

  if (isLoading || !rows.length)
    return (
      <>
        <PageHeader
          header='Team Members'
          subHeader='Manage your team and invite new members'
          pages={pages}
        />
        <main className='flex flex-col items-center justify-center p-8'>
          <FloxSpinner size='md' />
        </main>
      </>
    );

  const totalPages = Math.ceil(rows.length / rowsPerPage);
  const startItem = page * rowsPerPage + 1;
  const endItem = Math.min((page + 1) * rowsPerPage, rows.length);

  return (
    <>
      <PageHeader
        header='Team Members'
        subHeader='Manage your team and invite new members'
        pages={pages}
      >
        <Button
          onClick={() => setIsInviteDialogOpen(true)}
          intent='primary'
          size='sm'
          endIcon={<UserPlusIcon className='h-4 w-4' />}
          label='Invite'
        />
      </PageHeader>
      <main className='flex flex-col gap-6 p-4 pb-16 lg:px-8 lg:pb-24'>
        {/* Team Members Card */}
        <div className='bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden'>
          <div className='border-b border-gray-200 bg-gray-50 px-6 py-4'>
            <div className='flex items-center gap-3'>
              <UsersIcon className='h-5 w-5 text-gray-400' />
              <h2 className='text-lg font-semibold text-gray-900'>
                Team Members
              </h2>
              <span className='ml-auto text-sm text-gray-500'>
                {rows.length} {rows.length === 1 ? 'member' : 'members'}
              </span>
            </div>
          </div>

          <div className='overflow-x-auto'>
            <table className='min-w-full divide-y divide-gray-200'>
              <thead className='bg-gray-50'>
                <tr>
                  <th scope='col' className='w-12 px-3 py-3.5'>
                    <input
                      type='checkbox'
                      className='h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500'
                      checked={
                        rows.length > 0 && selected.length === rows.length
                      }
                      ref={(el) => {
                        if (el) {
                          el.indeterminate =
                            selected.length > 0 &&
                            selected.length < rows.length;
                        }
                      }}
                      onChange={handleSelectAllClick}
                      aria-label='select all team members'
                    />
                  </th>
                  {headCells.map((headCell) => (
                    <th
                      key={headCell.id}
                      scope='col'
                      className='px-3 py-3.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500'
                    >
                      <button
                        type='button'
                        className='group inline-flex items-center gap-1 hover:text-gray-700'
                        onClick={() => handleRequestSort(headCell.id)}
                      >
                        {headCell.label}
                        {orderBy === headCell.id ? (
                          order === 'desc' ? (
                            <ChevronDownIcon className='h-4 w-4' />
                          ) : (
                            <ChevronUpIcon className='h-4 w-4' />
                          )
                        ) : (
                          <ChevronUpIcon className='h-4 w-4 opacity-0 group-hover:opacity-50' />
                        )}
                      </button>
                    </th>
                  ))}
                  <th scope='col' className='relative px-3 py-3.5'>
                    <span className='sr-only'>Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-200 bg-white'>
                {visibleRows.map((row) => {
                  const isItemSelected = isSelected(row.name);

                  return (
                    <tr
                      key={row.email}
                      className={`hover:bg-gray-50 transition-colors cursor-pointer ${
                        isItemSelected ? 'bg-blue-50' : ''
                      }`}
                      onClick={(event) => handleClick(event, row.name)}
                    >
                      <td className='w-12 px-3 py-4'>
                        <input
                          type='checkbox'
                          className='h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500'
                          checked={isItemSelected}
                          onChange={() => {}}
                          aria-label={`select ${row.name}`}
                        />
                      </td>
                      <td className='whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900'>
                        {row.name}
                      </td>
                      <td className='whitespace-nowrap px-3 py-4 text-sm text-gray-500'>
                        {row.email}
                      </td>
                      <td className='whitespace-nowrap px-3 py-4 text-sm text-gray-500'>
                        {row.roles.join(', ')}
                      </td>
                      <td className='whitespace-nowrap px-3 py-4 text-sm'>
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                            row.status === 'Active'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {row.status}
                        </span>
                      </td>
                      <td className='whitespace-nowrap px-3 py-4 text-sm text-right'>
                        {localUser?.email !== row.email && (
                          <button
                            type='button'
                            onClick={(e) =>
                              handleStatusChange(e, row.email, row.status)
                            }
                            className={`inline-flex items-center rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                              row.status === 'Active'
                                ? 'bg-red-50 text-red-700 hover:bg-red-100'
                                : 'bg-green-50 text-green-700 hover:bg-green-100'
                            }`}
                          >
                            {row.status === 'Active'
                              ? 'Deactivate'
                              : 'Activate'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className='py-8 text-center text-sm text-gray-500'
                    >
                      No team members found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className='flex items-center justify-between border-t border-gray-200 bg-gray-50 px-4 py-3 sm:px-6'>
            <div className='flex items-center gap-2'>
              <label htmlFor='rows-per-page' className='text-sm text-gray-700'>
                Rows per page:
              </label>
              <select
                id='rows-per-page'
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}
                className='rounded-md border border-gray-300 bg-white py-1 pl-2 pr-8 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
              </select>
            </div>
            <div className='flex items-center gap-4'>
              <span className='text-sm text-gray-700'>
                {startItem}-{endItem} of {rows.length}
              </span>
              <div className='flex gap-1'>
                <button
                  type='button'
                  onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                  disabled={page === 0}
                  className='rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-500 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  <ChevronLeftIcon className='h-5 w-5' />
                </button>
                <button
                  type='button'
                  onClick={() =>
                    setPage((prev) => Math.min(totalPages - 1, prev + 1))
                  }
                  disabled={page >= totalPages - 1}
                  className='rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-500 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  <ChevronRightIcon className='h-5 w-5' />
                </button>
              </div>
            </div>
          </div>
        </div>

        <InviteTeamMemberDialog
          isOpen={isInviteDialogOpen}
          handleClose={() => setIsInviteDialogOpen(false)}
        />
      </main>
    </>
  );
}
